<template>
  <a-select
    show-search
    v-model="documentProxy"
    :mode="multiply ? 'multiple' : 'default'"
    :autoClearSearchValue="false"
    :allow-clear="true"
    :loading="loadingDocuments"
    :filter-option="false"
    :disabled="disabled"
    :placeholder="loadingDocuments ? 'Loading...' : 'Start typing to search Document'"
    @search="searchTemplates"
    @focus="focusTemplates"
    @blur="blurTemplates"
  >
    <a-select-option v-for="document in documents" :key="document.id" :value="document.id">
      {{ document.name }} </a-select-option
    ><template slot="dropdownRender" slot-scope="menu">
      <a-spin tip="Loading..." :spinning="loadingDocuments">
        <v-nodes :vnodes="menu" />
      </a-spin>
    </template>
  </a-select>
</template>

<script>
import { debounce } from '@/helpers/debounce'
import VNodes from '@/components/ui/v-nodes.vue'
import { notification, Select, Spin } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'

const { mapActions: documentActions } = createNamespacedHelpers('document')

export default {
  components: {
    'a-select': Select,
    'a-select-option': Select.Option,
    'a-spin': Spin,
    VNodes
  },
  props: {
    value: {
      type: Number || Array,
      default: undefined
    },
    disabled: {
      type: Boolean,
      default: false
    },
    multiply: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loadingDocuments: false,
      document: [],
      documents: [],

      searchQuery: ''
    }
  },
  watch: {
    searchQuery: {
      handler() {
        this.fetchDocuments()
      },
      immediate: true
    },
    value: {
      handler(newVal) {
        const alreadyExist = this.multiply
          ? (newVal ?? []).every(document => this.documents.find(item => item.id === document.id))
          : this.documents.find(item => item.id === newVal)

        const needFetch = this.multiply ? newVal?.length : !!newVal

        if (newVal && !alreadyExist && needFetch) {
          this.fetchDocument()
        }
      },
      deep: true,
      immediate: true
    }
  },
  computed: {
    documentProxy: {
      get() {
        if (Array.isArray(this.value)) {
          return this.value.map(item => item?.id)
        }
        return this.value ? (this.value?.id ?? this.value) : undefined
      },
      set(value) {
        if (value === undefined) {
          this.searchQuery = ''

          this.$emit('update', undefined)

          return
        }

        this.document = this.multiply
          ? this.documents.filter(item => value.includes(item.id))
          : this.documents.find(item => item.id === value)

        this.$emit('update', this.document)
      }
    }
  },
  methods: {
    ...documentActions(['getProjectDocuments', 'getProjectDocument']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    focusTemplates() {
      this.searchQuery = ''
    },

    blurTemplates() {
      this.searchQuery = ''
    },

    searchTemplates: debounce(function (value) {
      this.searchQuery = value
    }, 1000),

    async fetchDocuments() {
      this.loadingDocuments = true
      try {
        const data = await this.getProjectDocuments({
          filters: {
            name: this.searchQuery,
            page: 1,
            size: 10
          },
          cancelPreviousRequests: false
        })

        if (this.document.length) {
          const documentIds = new Set(this.document.map(item => item.id))

          this.documents = [...this.document, ...data.items.filter(item => !documentIds.has(item.id))]
        } else {
          this.documents = [...data.items]
        }
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loadingDocuments = false
      }
    },

    async fetchDocument() {
      try {
        const data = await this.getProjectDocuments({
          filters: {
            id: this.multiply || Array.isArray(this.value) ? this.value : [this.value],
            size: 1000
          },
          cancelPreviousRequests: false
        })

        this.document = data.items
        if (this.document.length) {
          const documentIds = new Set(this.document.map(item => item.id))

          this.documents = [...this.document, ...this.documents.filter(item => !documentIds.has(item.id))]
        }

        this.$emit('update', this.multiply ? this.document : this.document?.[0])
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
        this.document = []
        this.$emit('update', this.multiply ? this.document : undefined)
      }
    }
  }
}
</script>

<style scoped></style>
